import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useContext } from 'react';
import TagId from '~/constants/TagId';
import { useFinanceInfo } from '~/contexts/FinanceAndTradeContext';
import {
  useKeywordSuggestion,
  useSearchSource
} from '~/contexts/SearchEventsDataContext';
import { TileData } from '~/contexts/TileDataContext';
import UserContext from '~/contexts/UserContext';
import type { Vehicle } from '~/contracts';
import useGetSortByChanged from '~/hooks/useGetSortByChanged';
import usePriceDrop, { getPriceDropValue } from '~/hooks/usePriceDrop';
import {
  getDeliveryOrPickupByVehicle,
  maybeHasGreatDealTag
} from './helpers/ResultTile.helpers';
import useDataForAnalytics from './useDataForAnalytics';

// ! When adding values, ask if they need to be added for the mega event as well.
export default function useSendTileInfoEvent() {
  const { trackEvent } = useContext(AnalyticsContext);
  const searchSource = useSearchSource();
  const {
    appliedFilters,
    currentPage,
    isDefaultFilters,
    searchRequestId,
    sortBy,
    totalMatchedInventory,
    tradeInfo,
    vehicles
  } = useDataForAnalytics();
  const keywordSuggestion = useKeywordSuggestion();
  const financeInfo = useFinanceInfo();
  const { restrictedState: isPriceDropRestrictedState } = usePriceDrop();

  const {
    partnerId,
    userLocation: { zip }
  } = useContext(UserContext);
  const isInitialSortByRef = useGetSortByChanged();

  return useCallback(
    (vehicle: Partial<TileData> | undefined) => {
      const { dateAvailable, getItBy, routeId } = getDeliveryOrPickupByVehicle(
        vehicle ?? {}
      );

      const {
        locationId,
        price,
        previousPrice,
        vehicleId,
        vehicleTags,
        transportCost
      } = vehicle ?? {};
      const {
        apr,
        discounts,
        downPaymentDetails,
        feesMinusShipping,
        monthlyPayment,
        pricingErrorMessage,
        term,
        total,
        totalTaxes
      } = price ?? {};

      const downPayment = downPaymentDetails?.totalCashDue;
      const hasTerms = downPayment != null && monthlyPayment != null;

      const tileIndex = vehicles.findIndex(
        (v: Vehicle) => v.vehicleId === vehicleId
      );

      const eventData = {
        appliedFilters,
        currentPage,
        financeInfo,
        isEVTaxCredit:
          vehicle?.vehicleTags?.some(t => t.tagId === TagId.EVCredit) === true,
        isGreatDeal: maybeHasGreatDealTag(vehicle ?? {}),
        isOpenSearch: isDefaultFilters && isInitialSortByRef,
        isPriceDrop: getPriceDropValue(vehicle ?? {}),
        isPriceDropRestrictedState,
        isZeroDown: vehicle?.price?.downPayment === 0,
        keywordSuggestion,
        locationId,
        partnerId,
        searchRequestId,
        searchSource,
        sortBy,
        tileIndex,
        tileInfo: {
          apr,
          discounts,
          downPayment: hasTerms ? Number(downPayment) : downPayment,
          feesMinusShipping,
          getItBy,
          getItByDateIso: dateAvailable,
          hasTerms,
          monthlyPayment,
          pricingErrorMessage,
          previousPrice,
          routeId,
          shippingCost: transportCost,
          strickerPrice: total,
          totalTaxes,
          term
        },
        totalMatchedInventory,
        tradesInfo: {
          applyTradeIn: tradeInfo?.applyTradeIn,
          totalTradeInEquity: tradeInfo?.totalTradeInEquity,
          totalTradeInValue: tradeInfo?.totalTradeInValue
        },
        vehicleId,
        vehicleTags,
        zip
      };

      trackEvent('clicked-tile-finance-terms-info', eventData);

      datadogRum.addAction('clicked-tile-finance-terms-info', eventData);
    },
    [
      appliedFilters,
      currentPage,
      financeInfo,
      isDefaultFilters,
      isInitialSortByRef,
      keywordSuggestion,
      partnerId,
      searchRequestId,
      searchSource,
      sortBy,
      isPriceDropRestrictedState,
      totalMatchedInventory,
      trackEvent,
      tradeInfo?.applyTradeIn,
      tradeInfo?.totalTradeInEquity,
      tradeInfo?.totalTradeInValue,
      vehicles,
      zip
    ]
  );
}
